/** Journal (section 7) : filtres (action/email/établissement), tri par en-tête, export CSV,
 * purge avec confirmation. server_error en pastille rouge pleine, actions sensibles en bleu foncé. */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { adminApi } from '../../../services/api';
import { Button } from '../../../components/ui/kit';
import { useSortableTable, SortableTh } from '../useSortableTable';
import { ACTION_LABELS, SENSITIVE_ACTIONS } from '../constants';
import { dateTime } from '../format';

export default function Logs() {
  const [params] = useSearchParams();
  const [data, setData] = useState({ rows: [], total: 0, retention_days: 180 });
  const [tenants, setTenants] = useState([]);
  const [filters, setFilters] = useState({ action: params.get('action') || '', user: '', tenant_id: '' });

  const load = (f = filters) => adminApi.logs({ action: f.action || undefined, user: f.user || undefined,
    tenant_id: f.tenant_id || undefined, limit: 500 }).then(setData);
  useEffect(() => { adminApi.tenants().then(setTenants); load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (params.get('action')) { const f = { ...filters, action: params.get('action') }; setFilters(f); load(f); } /* eslint-disable-next-line */ }, [params]);

  const tname = (id) => tenants.find((t) => t.id === id)?.name || (id ? '—' : '—');
  const rows = data.rows;
  const getters = { date: (r) => r.date, action: (r) => r.action, user: (r) => r.user_email,
    tenant: (r) => tname(r.tenant_id), detail: (r) => r.detail, ip: (r) => r.ip };
  const { sorted, sort, toggle } = useSortableTable(rows, getters, { key: 'date', dir: 'desc' });

  const clearFilters = () => { const f = { action: '', user: '', tenant_id: '' }; setFilters(f); load(f); };
  const exportCsv = () => adminApi.exportLogsCsv({ action: filters.action || undefined, user: filters.user || undefined,
    tenant_id: filters.tenant_id || undefined }, `cavorama-journal-${new Date().toISOString().slice(0, 10)}.csv`)
    .then(() => toast.success('Journal exporté')).catch((e) => toast.error(e.message));
  const purge = () => { if (!window.confirm("Effacer TOUT le journal ? Exportez-le d'abord si besoin. Cette action est irréversible.")) return;
    adminApi.purgeLogs().then((r) => { toast.success(`${r.cleared} entrées effacées`); load(); }).catch((e) => toast.error(e.message)); };

  const actions = useMemo(() => Object.keys(ACTION_LABELS), []);
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleRow = (key) => setExpanded((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });

  return (
    <div data-testid="admin-logs">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-title text-3xl">Journal</h1>
        <div className="flex gap-2">
          <Button onClick={exportCsv} data-testid="logs-export">Exporter CSV</Button>
          <Button onClick={purge} className="text-red-700 border-red-700/50" data-testid="logs-purge">Purger</Button>
        </div>
      </div>

      <form className="flex flex-wrap items-center gap-2 mb-3" onSubmit={(e) => { e.preventDefault(); load(); }}>
        <select className="vd-input" style={{ width: 'auto' }} value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} data-testid="logs-filter-action">
          <option value="">Toutes les actions</option>
          {actions.map((a) => <option key={a} value={a}>{ACTION_LABELS[a]}</option>)}
        </select>
        <input className="vd-input" style={{ width: 'auto', flex: '0 1 200px' }} placeholder="Email utilisateur" value={filters.user} onChange={(e) => setFilters({ ...filters, user: e.target.value })} data-testid="logs-filter-user" />
        <select className="vd-input" style={{ width: 'auto' }} value={filters.tenant_id} onChange={(e) => setFilters({ ...filters, tenant_id: e.target.value })} data-testid="logs-filter-tenant">
          <option value="">Tous les établissements</option>
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <Button type="submit" variant="primary" data-testid="logs-filter-apply">Filtrer</Button>
        <button type="button" className="vd-link" onClick={clearFilters}>Effacer</button>
        <span className="text-xs text-n600 ml-auto">{data.total} entrées · conservation {data.retention_days} jours</span>
      </form>

      <div className="overflow-x-auto">
        <table className="w-full text-sm adm-table">
          <thead><tr className="text-left text-n600 border-b border-hair">
            <SortableTh label="Date" sortKey="date" sort={sort} toggle={toggle} />
            <SortableTh label="Action" sortKey="action" sort={sort} toggle={toggle} />
            <SortableTh label="Utilisateur" sortKey="user" sort={sort} toggle={toggle} />
            <SortableTh label="Établissement" sortKey="tenant" sort={sort} toggle={toggle} />
            <SortableTh label="Détail" sortKey="detail" sort={sort} toggle={toggle} />
            <SortableTh label="IP" sortKey="ip" sort={sort} toggle={toggle} />
          </tr></thead>
          <tbody>
            {sorted.map((l, i) => {
              const key = `${l.date}|${l.action}|${l.ip || ''}|${i}`;
              const isOpen = expanded.has(key);
              const hasDetail = !!(l.detail && l.detail.length);
              return (
                <React.Fragment key={key}>
                  <tr className={`border-b border-hair/30 align-middle h-[42px] ${hasDetail ? 'cursor-pointer' : ''}`}
                    onClick={() => hasDetail && toggleRow(key)} data-testid={`log-row-${i}`}>
                    <td className="px-2 whitespace-nowrap adm-mono text-xs">{dateTime(l.date)}</td>
                    <td className="px-1">
                      {l.action === 'server_error'
                        ? <span className="adm-badge-error px-2 py-0.5 rounded adm-mono text-xs">{l.action}</span>
                        : <span className={`adm-mono text-xs ${SENSITIVE_ACTIONS.has(l.action) ? 'text-[color:var(--accent-800)] font-semibold' : ''}`}>{l.action}</span>}
                    </td>
                    <td className="px-1">{l.user_email || '—'}{l.via_superadmin ? <span className="text-xs text-n500"> (via super-admin)</span> : ''}</td>
                    <td className="px-1 whitespace-nowrap">{tname(l.tenant_id)} {l.tenant_id ? <span className="adm-mono text-[10px] text-n500">· {l.tenant_id.slice(0, 8)}</span> : ''}</td>
                    <td className="px-1 max-w-[320px] truncate" title={l.detail || ''}>{l.detail || ''}</td>
                    <td className="px-1 adm-mono text-xs">{l.ip || '—'}</td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-hair/30" data-testid={`log-detail-${i}`}>
                      <td></td>
                      <td colSpan={5} className="px-1 py-2 text-sm text-n700 whitespace-pre-wrap break-words">
                        <span className="text-[10px] uppercase tracking-wide text-n500 mr-2">Détail</span>{l.detail}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
